/**
 * Settings API client — wired to real backend endpoints (B slice 6).
 *
 *   - GET  /v1/pricing/tariffs             (service-pricing :3030)
 *   - GET  /v1/notifications/templates     (service-notification :3020)
 *   - PUT  /v1/notifications/templates/:id (service-notification :3020)
 *   - GET  /v1/platform-settings           (service-audit :3010)
 *   - PUT  /v1/platform-settings/:key      (service-audit :3010)
 */
import { env } from '../env';

import { request } from './client';

// ---------------------------------------------------------------------------
// Pricing tariffs
// ---------------------------------------------------------------------------

export interface PricingTariff {
  readonly id: string;
  readonly originZoneCode: string;
  readonly destZoneCode: string;
  readonly serviceClassCode: string;
  readonly weightCapGrams: number;
  readonly basePrice: string;
  readonly currency: string;
  readonly perGramSurcharge: string;
  readonly validFrom: string;
  readonly validUntil: string | null;
}

export interface ListTariffsResponse {
  readonly items: readonly PricingTariff[];
}

export async function listPricingTariffs(): Promise<ListTariffsResponse> {
  return request<ListTariffsResponse>(env.servicePricingBaseUrl, '/v1/pricing/tariffs', {
    method: 'GET',
  });
}

export interface CreatePricingTariffInput {
  readonly originZoneCode: string;
  readonly destZoneCode: string;
  readonly serviceClassCode: string;
  readonly weightCapGrams: number;
  readonly basePrice: string;
  readonly currency: string;
  readonly perGramSurcharge: string;
  readonly validFrom: string;
  readonly validUntil?: string | null;
}

export async function createPricingTariff(
  input: CreatePricingTariffInput,
): Promise<PricingTariff> {
  return request<PricingTariff>(
    env.servicePricingBaseUrl,
    '/v1/pricing/tariffs',
    { method: 'POST', body: input },
  );
}

export async function updatePricingTariff(
  id: string,
  patch: Partial<CreatePricingTariffInput>,
): Promise<PricingTariff> {
  return request<PricingTariff>(
    env.servicePricingBaseUrl,
    `/v1/pricing/tariffs/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deletePricingTariff(id: string): Promise<void> {
  await request<unknown>(
    env.servicePricingBaseUrl,
    `/v1/pricing/tariffs/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Pricing zones + service classes (used by PricingConfigPage dropdowns)
// ---------------------------------------------------------------------------

export interface PricingZone {
  readonly code: string;
  readonly name: string;
  readonly countryCode: string;
}

export interface ListPricingZonesResponse {
  readonly items: readonly PricingZone[];
}

export async function listPricingZones(): Promise<ListPricingZonesResponse> {
  return request<ListPricingZonesResponse>(
    env.servicePricingBaseUrl,
    '/v1/pricing/zones',
    { method: 'GET' },
  );
}

/** Admin-facing zone DTO (includes id + active). */
export interface PricingZoneAdmin {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly countryCode: string;
  readonly active: boolean;
}

export interface ListPricingZonesAdminResponse {
  readonly items: readonly PricingZoneAdmin[];
}

export async function listPricingZonesAdmin(): Promise<ListPricingZonesAdminResponse> {
  return request<ListPricingZonesAdminResponse>(
    env.servicePricingBaseUrl,
    '/v1/pricing/zones/admin',
    { method: 'GET' },
  );
}

export interface CreatePricingZoneInput {
  readonly code: string;
  readonly name: string;
  readonly countryCode?: string;
  readonly active: boolean;
}

export async function createPricingZone(
  input: CreatePricingZoneInput,
): Promise<PricingZoneAdmin> {
  return request<PricingZoneAdmin>(
    env.servicePricingBaseUrl,
    '/v1/pricing/zones',
    { method: 'POST', body: input },
  );
}

export async function updatePricingZone(
  id: string,
  patch: Partial<CreatePricingZoneInput>,
): Promise<PricingZoneAdmin> {
  return request<PricingZoneAdmin>(
    env.servicePricingBaseUrl,
    `/v1/pricing/zones/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deletePricingZone(id: string): Promise<void> {
  await request<unknown>(
    env.servicePricingBaseUrl,
    `/v1/pricing/zones/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export interface PricingServiceClass {
  readonly code: string;
  readonly displayName: string;
  readonly slaHours: number;
}

export interface ListPricingServiceClassesResponse {
  readonly items: readonly PricingServiceClass[];
}

export async function listPricingServiceClasses(): Promise<ListPricingServiceClassesResponse> {
  return request<ListPricingServiceClassesResponse>(
    env.servicePricingBaseUrl,
    '/v1/pricing/service-classes',
    { method: 'GET' },
  );
}

/** Admin-facing service-class DTO (includes id + active). */
export interface ServiceClassAdmin {
  readonly id: string;
  readonly code: string;
  readonly displayName: string;
  readonly slaHours: number;
  readonly active: boolean;
}

export interface ListServiceClassesAdminResponse {
  readonly items: readonly ServiceClassAdmin[];
}

export async function listServiceClassesAdmin(): Promise<ListServiceClassesAdminResponse> {
  return request<ListServiceClassesAdminResponse>(
    env.servicePricingBaseUrl,
    '/v1/pricing/service-classes/admin',
    { method: 'GET' },
  );
}

export interface CreateServiceClassInput {
  readonly code: string;
  readonly displayName: string;
  readonly slaHours: number;
  readonly active: boolean;
}

export async function createServiceClass(
  input: CreateServiceClassInput,
): Promise<ServiceClassAdmin> {
  return request<ServiceClassAdmin>(
    env.servicePricingBaseUrl,
    '/v1/pricing/service-classes',
    { method: 'POST', body: input },
  );
}

export async function updateServiceClass(
  id: string,
  patch: Partial<CreateServiceClassInput>,
): Promise<ServiceClassAdmin> {
  return request<ServiceClassAdmin>(
    env.servicePricingBaseUrl,
    `/v1/pricing/service-classes/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deleteServiceClass(id: string): Promise<void> {
  await request<unknown>(
    env.servicePricingBaseUrl,
    `/v1/pricing/service-classes/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Pricing surcharges — admin CRUD
//
//   GET    /v1/pricing/surcharges/admin   — list (active + inactive)
//   POST   /v1/pricing/surcharges         — create
//   PATCH  /v1/pricing/surcharges/:id     — update
//   DELETE /v1/pricing/surcharges/:id     — delete (hard-delete; no FK refs)
// ---------------------------------------------------------------------------

/** Surcharge condition predicate (mirrors the service-pricing union). */
export type SurchargeCondition =
  | { readonly kind: 'WEIGHT_GT'; readonly valueGrams: number }
  | { readonly kind: 'SERVICE_CLASS_IN'; readonly codes: readonly string[] }
  | { readonly kind: 'ALWAYS' };

/** Admin-facing surcharge DTO (includes id + active + validity window). */
export interface PricingSurchargeAdmin {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly amount: string;
  readonly isPercentage: boolean;
  readonly condition: SurchargeCondition | null;
  readonly active: boolean;
  readonly validFrom: string;
  readonly validUntil: string | null;
}

export interface ListSurchargesAdminResponse {
  readonly items: readonly PricingSurchargeAdmin[];
}

export async function listSurchargesAdmin(): Promise<ListSurchargesAdminResponse> {
  return request<ListSurchargesAdminResponse>(
    env.servicePricingBaseUrl,
    '/v1/pricing/surcharges/admin',
    { method: 'GET' },
  );
}

export interface CreateSurchargeInput {
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly amount: string;
  readonly isPercentage?: boolean;
  readonly condition?: SurchargeCondition | null;
  readonly active: boolean;
  readonly validFrom: string;
  readonly validUntil?: string | null;
}

export async function createSurcharge(
  input: CreateSurchargeInput,
): Promise<PricingSurchargeAdmin> {
  return request<PricingSurchargeAdmin>(
    env.servicePricingBaseUrl,
    '/v1/pricing/surcharges',
    { method: 'POST', body: input },
  );
}

export async function updateSurcharge(
  id: string,
  patch: Partial<CreateSurchargeInput>,
): Promise<PricingSurchargeAdmin> {
  return request<PricingSurchargeAdmin>(
    env.servicePricingBaseUrl,
    `/v1/pricing/surcharges/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deleteSurcharge(id: string): Promise<void> {
  await request<unknown>(
    env.servicePricingBaseUrl,
    `/v1/pricing/surcharges/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Notification templates
// ---------------------------------------------------------------------------

export interface NotificationTemplate {
  readonly id: string;
  readonly key: string;
  readonly name: string;
  readonly channel: 'SMS' | 'EMAIL' | 'PUSH';
  readonly module: string;
  readonly subject?: string | null;
  readonly body: string;
  readonly active?: boolean;
  readonly description?: string | null;
  readonly version?: number;
  readonly updatedAt?: string;
}

export interface ListNotificationTemplatesResponse {
  readonly items: readonly NotificationTemplate[];
}

export async function listNotificationTemplates(
  channel?: 'SMS' | 'EMAIL' | 'PUSH',
): Promise<ListNotificationTemplatesResponse> {
  return request<ListNotificationTemplatesResponse>(
    env.serviceNotificationBaseUrl,
    '/v1/notifications/templates',
    { method: 'GET', query: channel ? { channel } : undefined },
  );
}

export async function updateNotificationTemplate(
  id: string,
  patch: { body: string; subject?: string | null; description?: string | null; active?: boolean },
): Promise<NotificationTemplate> {
  return request<NotificationTemplate>(
    env.serviceNotificationBaseUrl,
    `/v1/notifications/templates/${encodeURIComponent(id)}`,
    { method: 'PUT', body: patch },
  );
}

// ---------------------------------------------------------------------------
// Notification dead-letter queue (service-notification :3020)
//   GET  /v1/notifications/dlq?topic=&from=&to=&page=  — Notifications/readDlq
//   POST /v1/notifications/dlq/:id/replay              — Notifications/replayDlq
// ---------------------------------------------------------------------------

export type NotificationDlqFailureKind =
  | 'VALIDATION_FAIL'
  | 'DESERIALIZE_FAIL'
  | 'HANDLER_ERROR';

export interface NotificationDlqEntry {
  readonly id: string;
  readonly sourceTopic: string;
  readonly partition: number;
  /** Kafka offset — BigInt serialised as a decimal string. */
  readonly offset: string;
  readonly key: string | null;
  readonly payload: unknown;
  readonly failureReason: string;
  readonly failureKind: NotificationDlqFailureKind;
  readonly receivedAt: string;
  readonly dlqAt: string;
  readonly replayCount: number;
  readonly replayedAt: string | null;
}

export interface ListNotificationDlqResponse {
  readonly items: readonly NotificationDlqEntry[];
  readonly page: number;
  readonly pageSize: number;
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export interface ListNotificationDlqQuery {
  readonly topic?: string;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
}

export async function listNotificationDlq(
  query: ListNotificationDlqQuery = {},
): Promise<ListNotificationDlqResponse> {
  return request<ListNotificationDlqResponse>(
    env.serviceNotificationBaseUrl,
    '/v1/notifications/dlq',
    {
      method: 'GET',
      query: {
        ...(query.topic ? { topic: query.topic } : {}),
        ...(query.from ? { from: query.from } : {}),
        ...(query.to ? { to: query.to } : {}),
        ...(query.page !== undefined ? { page: String(query.page) } : {}),
      },
    },
  );
}

export interface ReplayNotificationDlqResponse {
  readonly ok: true;
  readonly replayCount: number;
}

export async function replayNotificationDlqEntry(
  id: string,
): Promise<ReplayNotificationDlqResponse> {
  return request<ReplayNotificationDlqResponse>(
    env.serviceNotificationBaseUrl,
    `/v1/notifications/dlq/${encodeURIComponent(id)}/replay`,
    { method: 'POST' },
  );
}

// ---------------------------------------------------------------------------
// Platform settings (system parameters)
// ---------------------------------------------------------------------------

export interface PlatformSetting {
  readonly key: string;
  readonly value: unknown;
  readonly description?: string | null;
  readonly updatedAt: string;
  readonly updatedBy?: string | null;
}

export interface ListPlatformSettingsResponse {
  readonly items: readonly PlatformSetting[];
}

export async function listPlatformSettings(): Promise<ListPlatformSettingsResponse> {
  return request<ListPlatformSettingsResponse>(
    env.serviceAuditBaseUrl,
    '/v1/platform-settings',
    { method: 'GET' },
  );
}

export async function upsertPlatformSetting(
  key: string,
  value: unknown,
  description?: string,
  updatedBy?: string,
): Promise<PlatformSetting> {
  return request<PlatformSetting>(
    env.serviceAuditBaseUrl,
    `/v1/platform-settings/${encodeURIComponent(key)}`,
    { method: 'PUT', body: { value, description, updatedBy } },
  );
}

// ---------------------------------------------------------------------------
// Settings collections — generic JSON-array registries (discount / fraud /
// transaction limits / revenue share / recipient accounts / tier-service
// overrides / gov-service overrides). Each registry is fetched + replaced as
// a single document via /v1/settings-collections/:key.
// ---------------------------------------------------------------------------

export type SettingsCollectionKey =
  | 'discount_rules'
  | 'fraud_rules'
  | 'transaction_limits'
  | 'revenue_share_rules'
  | 'recipient_accounts'
  | 'tier_service_overrides'
  | 'gov_service_overrides';

export interface SettingsCollection<T = unknown> {
  readonly key: string;
  readonly items: readonly T[];
  readonly description?: string | null;
  readonly updatedAt: string;
  readonly updatedBy?: string | null;
}

export async function getSettingsCollection<T = unknown>(
  key: SettingsCollectionKey,
): Promise<SettingsCollection<T>> {
  return request<SettingsCollection<T>>(
    env.serviceAuditBaseUrl,
    `/v1/settings-collections/${encodeURIComponent(key)}`,
    { method: 'GET' },
  );
}

export async function putSettingsCollection<T = unknown>(
  key: SettingsCollectionKey,
  items: readonly T[],
  description?: string,
): Promise<SettingsCollection<T>> {
  return request<SettingsCollection<T>>(
    env.serviceAuditBaseUrl,
    `/v1/settings-collections/${encodeURIComponent(key)}`,
    { method: 'PUT', body: { items, description } },
  );
}

// ---------------------------------------------------------------------------
// Outlet admin (service-customer)
// ---------------------------------------------------------------------------

export interface OutletInput {
  readonly outletCode: string;
  readonly name: string;
  readonly addressLine1: string;
  readonly addressLine2?: string | null;
  readonly city: string;
  readonly lga: string;
  readonly state: string;
  readonly postalCode?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly latitude?: string | null;
  readonly longitude?: string | null;
  readonly services?: readonly string[];
  readonly openingHours?: Record<string, string>;
  readonly isActive?: boolean;
}

export interface OutletDto {
  readonly id: string;
  readonly outletCode: string;
  readonly name: string;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string;
  readonly lga: string;
  readonly state: string;
  readonly postalCode: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly latitude: string | null;
  readonly longitude: string | null;
  readonly services: readonly string[];
  readonly openingHours: Record<string, string>;
  readonly isActive: boolean;
}

export async function createOutlet(input: OutletInput): Promise<OutletDto> {
  return request<OutletDto>(
    env.serviceCustomerBaseUrl,
    '/v1/outlets/admin',
    { method: 'POST', body: input },
  );
}

export async function updateOutlet(
  id: string,
  patch: Partial<OutletInput>,
): Promise<OutletDto> {
  return request<OutletDto>(
    env.serviceCustomerBaseUrl,
    `/v1/outlets/admin/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
}

export async function deleteOutlet(id: string): Promise<void> {
  await request<unknown>(
    env.serviceCustomerBaseUrl,
    `/v1/outlets/admin/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
}

export interface ListOutletsResponse {
  readonly items: readonly OutletDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export async function listOutletsAdmin(
  query: { q?: string; state?: string; pageSize?: number; page?: number } = {},
): Promise<ListOutletsResponse> {
  return request<ListOutletsResponse>(
    env.serviceCustomerBaseUrl,
    '/v1/outlets',
    {
      method: 'GET',
      query: {
        ...(query.q ? { q: query.q } : {}),
        ...(query.state ? { state: query.state } : {}),
        page: String(query.page ?? 1),
        pageSize: String(query.pageSize ?? 50),
      },
    },
  );
}
